import type { ScoredRecord, VectorRecord, VectorStore } from './types.js';

/** Dot product (== cosine when both inputs are L2-normalized). */
function dot(a: readonly number[], b: readonly number[]): number {
  const n = Math.min(a.length, b.length);
  let sum = 0;
  for (let i = 0; i < n; i++) sum += (a[i] ?? 0) * (b[i] ?? 0);
  return sum;
}

/**
 * In-memory flat vector index: brute-force cosine over all records. Fine for a
 * single repo's chunk set (the embedded/dev path). The prod path swaps in a
 * Qdrant-backed store behind {@link VectorStore}.
 */
export class FlatVectorStore<P> implements VectorStore<P> {
  private records: VectorRecord<P>[] = [];

  async upsert(records: ReadonlyArray<VectorRecord<P>>): Promise<void> {
    const byId = new Map(this.records.map((r) => [r.id, r] as const));
    for (const r of records) byId.set(r.id, r);
    this.records = [...byId.values()];
  }

  async query(vector: readonly number[], topK: number): Promise<ScoredRecord<P>[]> {
    return this.records
      .map((r) => ({ id: r.id, score: dot(vector, r.vector), payload: r.payload }))
      .sort((a, b) => b.score - a.score)
      .slice(0, Math.max(0, topK));
  }

  async size(): Promise<number> {
    return this.records.length;
  }

  async clear(): Promise<void> {
    this.records = [];
  }

  /** The records, for persistence. */
  toRecords(): VectorRecord<P>[] {
    return [...this.records];
  }

  static fromRecords<P>(records: ReadonlyArray<VectorRecord<P>>): FlatVectorStore<P> {
    const store = new FlatVectorStore<P>();
    store.upsert(records);
    return store;
  }
}
